import React, { useState } from 'react';
import Button from '../components/ui/Button';
import '../styles/pages/ContactPage.css';

const CONTACT_API_URL = 'https://5e478a5ux7.execute-api.eu-west-2.amazonaws.com/dev/sendEmail';

const initialFormData = {
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '',
};

const ContactPage = () => {
    const [formData, setFormData] = useState(initialFormData);
    const [formErrors, setFormErrors] = useState({});
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (formSubmitted) {
            setFormSubmitted(false);
        }

        if (submissionError) {
            setSubmissionError(false);
        }
    };

    const validateForm = () => {
        const errors = {};
        const trimmedEmail = formData.email.trim();
        const trimmedMessage = formData.message.trim();
        const trimmedName = formData.name.trim();
        const trimmedSubject = formData.subject.trim();

        if (!trimmedName) {
            errors.name = 'Please enter your name.';
        }

        if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            errors.email = 'Please enter a valid email address.';
        }

        if (!trimmedSubject) {
            errors.subject = 'Please select a subject.';
        }

        if (trimmedMessage.length < 10) {
            errors.message = 'Message must be at least 10 characters long.';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting || !validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmissionError(false);
        setFormSubmitted(false);

        try {
            const response = await fetch(CONTACT_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    subject: formData.subject.trim(),
                    message: formData.message.trim(),
                    website: formData.website,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to send message');
            }

            setFormData(initialFormData);
            setFormErrors({});
            setFormSubmitted(true);
        } catch (error) {
            console.error('Error sending contact form:', error);
            setSubmissionError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            <h2>Contact Us</h2>

            {formSubmitted && (
                <p className="success-message">
                    Thank you for your message! We&apos;ll get back to you shortly.
                </p>
            )}

            {submissionError && (
                <p className="error-message form-status-error">
                    An error occurred while sending your message. Please try again later.
                </p>
            )}

            <form onSubmit={handleSubmit} noValidate>
                <div className="honeypot-field" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        autoComplete="off"
                        tabIndex={-1}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        maxLength={100}
                    />
                    {formErrors.name && <p className="error-message">{formErrors.name}</p>}
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        maxLength={254}
                    />
                    {formErrors.email && <p className="error-message">{formErrors.email}</p>}
                </div>

                <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                    >
                        <option value="" disabled>
                            Select a subject
                        </option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Support">Support</option>
                        <option value="Feedback">Feedback</option>
                    </select>
                    {formErrors.subject && <p className="error-message">{formErrors.subject}</p>}
                </div>

                <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={5000}
                    />
                    {formErrors.message && <p className="error-message">{formErrors.message}</p>}
                </div>

                <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    className="contact-submit-button"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
            </form>
        </div>
    );
};

export default ContactPage;